import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Tab, Tabs, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';

export function AssetPmRecordList({ slug, list, query, onQuery, loading = !list }) {
    const [searchInput, setSearchInput] = useState(query.search || '');
    const base = `/tpm/${slug}/preventive-maintenance`;
    const firstEntry = list?.items.length ? (list.page - 1) * list.pageSize + 1 : 0;
    const lastEntry = list?.items.length ? firstEntry + list.items.length - 1 : 0;

    useEffect(() => {
        setSearchInput(query.search || '');
    }, [query.search]);

    useEffect(() => {
        if (searchInput === (query.search || '')) return;

        const timer = setTimeout(() => {
            onQuery({ ...query, page: 1, search: searchInput });
        }, 300);

        return () => clearTimeout(timer);
    }, [searchInput, query, onQuery]);

    const filter = (field, value) => {
        if ((query[field] || '') === value) return;

        const next = { ...query, page: 1, [field]: value };
        if (field === 'dueState' && value === 'upcoming') {
            next.status = '';
        }

        onQuery(next);
    };

    return (
        <>
            <Tabs
                activeKey={query.frequency || 'all'}
                onSelect={(value) => filter('frequency', value === 'all' ? '' : value)}
                className="mb-3"
            >
                <Tab eventKey="all" title="All frequencies" />
                {['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'].map((frequency) => (
                    <Tab key={frequency} eventKey={frequency} title={frequency} />
                ))}
            </Tabs>
            <div className="d-flex flex-wrap gap-2 mb-3">
                <div className="w-100">
                    <Row className="g-2">
                        <Col xs={12} md={4}>
                            <Form.Control
                                aria-label="Asset name or number"
                                placeholder="Asset name or number (optional)"
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.target.value)}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Select
                                aria-label="Due state"
                                value={query.dueState || ''}
                                onChange={(event) => filter('dueState', event.target.value)}
                            >
                                <option value="">All records</option>
                                <option value="upcoming">Upcoming schedules</option>
                                <option value="due">Due today</option>
                                <option value="overdue">Overdue</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Select
                                aria-label="Record status"
                                disabled={query.dueState === 'upcoming'}
                                value={query.status || ''}
                                onChange={(event) => filter('status', event.target.value)}
                            >
                                <option value="">All statuses</option>
                                <option value="Opened">Opened</option>
                                <option value="Closed">Closed</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </div>
                <div className="w-100">
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Group controlId={`${slug}-pm-due-from`}>
                                <Form.Label>Due from</Form.Label>
                                <Form.Control
                                    className="asset-pm-date-filter"
                                    type="date"
                                    value={query.from || ''}
                                    onChange={(event) => filter('from', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group controlId={`${slug}-pm-due-to`}>
                                <Form.Label>Due to</Form.Label>
                                <Form.Control
                                    className="asset-pm-date-filter"
                                    type="date"
                                    value={query.to || ''}
                                    onChange={(event) => filter('to', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>
            {loading && <DatacoLoadingIndicator label="Loading maintenance records…" />}
            {list && (
                <>
                    <Table responsive striped>
                        <thead>
                            <tr>
                                <th>Asset</th>
                                <th>Frequency</th>
                                <th>Due date</th>
                                <th>State</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.items.map((item) => (
                                <tr key={`${item.itemType}:${item.scheduleId}:${item.dueDate}`}>
                                    <td>
                                        {item.asset.name ||
                                            item.asset.machine ||
                                            item.asset.testerNo ||
                                            item.asset.jigFixNo}
                                    </td>
                                    <td>{item.frequency}</td>
                                    <td>{item.dueDate}</td>
                                    <td>
                                        {item.status || 'Scheduled'} · {item.dueState}
                                        {item.daysOverdue ? ` (${item.daysOverdue} days)` : ''}
                                    </td>
                                    <td>
                                        {item.itemType === 'schedule' ? (
                                            <Link
                                                to={`${base}/checklists/${encodeURIComponent(item.asset.id)}`}
                                            >
                                                View checklist
                                            </Link>
                                        ) : (
                                            <Link
                                                to={`${base}/records/${encodeURIComponent(item.id)}`}
                                            >
                                                Open record
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {!list.items.length && <p>No maintenance matches these filters.</p>}
                    <p>
                        {firstEntry}–{lastEntry} of {list.total} entries · Page {list.page}
                    </p>
                    <Button
                        className="me-2"
                        disabled={list.page === 1}
                        onClick={() => onQuery({ ...query, page: list.page - 1 })}
                    >
                        Previous
                    </Button>
                    <Button
                        disabled={list.page * list.pageSize >= list.total}
                        onClick={() => onQuery({ ...query, page: list.page + 1 })}
                    >
                        Next
                    </Button>
                </>
            )}
        </>
    );
}
